import { ArrowUpCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import AddButton from "../../../../components/AddButton";
import DownloadButton from "../../../../components/DownloadButton";
import MassUploadButton from "../../../../components/MassUploadButton";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { prodCodeAddState, prodCodeMassUpload } from "../atom";
import Modal from "components/Modal";
import SubmitHookForm from "components/SubmitHookForm";
import CancelButton from "components/CancelButton";
import { toast } from "components/toastNotification";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import useProcessKeeperRecords from "../hooks/useProcessKeeperRecords";
import * as XLSX from "xlsx";

export const ProducerCodeActionGroup = ({
  handleExportButton,
  isLoadingExport,
}) => {
  const [, setIsAddState] = useRecoilState(prodCodeAddState);
  const [, setMassUpload] = useRecoilState(prodCodeMassUpload);
  const addPermission =
    PERMISSIONS.permission_carrieragreemnentmgmt_producercode_add;
  const viewPermission =
    PERMISSIONS.permission_carrieragreemnentmgmt_producercode_view;
  const userPermissions = useRecoilValue(permissionsSelector);
  const [showKeeperUpload, setShowKeeperUpload] = useState(false);
  const [keeperRecords, setKeeperRecords] = useState<File | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { mutate: processKeeperRecords } = useProcessKeeperRecords({
    setIsSaving,
    setKeeperRecords,
  });

  const handleFileChange = (e) => {
    const fileInput = e.target;
    const selectedFile = fileInput.files[0];

    if (selectedFile) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const fileContent = event.target?.result;

        try {
          const wb = XLSX.read(fileContent, { type: "binary" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });
          const xData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);

          const hasValidData = xData.some(
            (row) =>
              row["title"] !== undefined &&
              row["title"] !== "" &&
              row["Office code"] !== undefined &&
              row["Office code"] !== ""
          );

          // Check if Excel file is empty
          if (!rows || rows.length === 0) {
            toast.fire(
              "Uploading Producer Code Failed",
              "Invalid file, please check file and re-upload again",
              "error"
            );
            return;
          }

          // Check if header row exists
          const headerRow = rows[0] || [];
          if (headerRow.length === 0) {
            toast.fire(
              "Uploading Producer Code Failed",
              "Invalid file, please check file and re-upload again",
              "error"
            );
            return;
          }

          //Check if a single header is missing, so that it accepts the same template
          const requiredHeaders = [
            "title",
            "uid",
            "login",
            "password",
            "notes",
            "$type",
            "last_modified",
            "custom_fields",
            "schema",
            "Folder",
            "source_file",
            "login_url",
            "Store code",
            "officeId",
            "Office code",
            "officeName",
            "officeType",
            "brandName",
            "stateCode",
            "zipCode",
            "country",
            "officeStatus",
            "isActive",
            "officeManagerName",
            "legalEntityName",
          ];

          const missingHeaders = requiredHeaders.filter(
            (header) => !headerRow.includes(header)
          );

          //if there is a missing header, add a toaster
          if (missingHeaders.length > 0) {
            toast.fire(
              "Uploading Producer Code Failed",
              "Invalid file, please check file and re-upload again",
              "error"
            );
            return;
          }

          //only check if title and office code has no value
          if (!hasValidData) {
            toast.fire(
              "Uploading Producer Code Failed",
              "Invalid file, please check file and re-upload again",
              "error"
            );
            return;
          }

          setKeeperRecords(selectedFile);
        } catch (error) {
          toast.fire(
            "Uploading Producer Code Failed",
            "Invalid file, please check file and re-upload again",
            "error"
          );
        } finally {
          fileInput.value = "";
        }
      };
      reader.readAsBinaryString(selectedFile);
    }
  };

  const handleSubmit = () => {
    setShowKeeperUpload(false);
    setOpenConfirm(false);

    if (keeperRecords) {
      processKeeperRecords(keeperRecords);
    } else {
      toast.fire("Error", "No file selected", "error");
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="gap-x-2 flex items-center">
        {userPermissions.includes(addPermission) ? (
          <AddButton onClick={() => setIsAddState(true)} buttonText="Add" />
        ) : (
          ""
        )}
        <MassUploadButton onClickFunction={() => setMassUpload(true)}>
          <ArrowUpCircleIcon className="h-4 w-4 mr-2" />
          <p>Mass Upload</p>
        </MassUploadButton>
        <MassUploadButton
          onClickFunction={() => setShowKeeperUpload(true)}
          disabled={isSaving}
        >
          <ArrowUpCircleIcon className="h-4 w-4 mr-2" />
          <p>{isSaving ? "Keeper Upload..." : "Keeper Upload"}</p>
        </MassUploadButton>
        {showKeeperUpload && (
          <Modal
            isCloseHide={true}
            modalFilterStat=""
            className=""
            width={"w-200"}
            title={""}
            isOpen={showKeeperUpload}
            onClose={undefined}
            hasCloseButton={false}
          >
            <div className="container">
              <div className="flex justify-between gap-4">
                <div className="ml-2 mr-3 text-base text-primary font-bold">
                  <p>Keeper Upload</p>
                </div>
                <div className="flex justify-between gap-2 mr-2 ml-auto">
                  <SubmitHookForm
                    type="button"
                    buttonText={isSaving ? "Saving..." : "Save"}
                    disabled={isSaving}
                    onClick={() => {
                      if (keeperRecords === null) {
                        toast.fire(
                          "Information",
                          "Please upload excel file to proceed.",
                          "info"
                        );
                      } else {
                        setOpenConfirm(true);
                        setIsSaving(true);
                      }
                    }}
                  />
                  <CancelButton
                    onClick={() => {
                      setShowKeeperUpload(false);
                      setKeeperRecords(null);
                      setOpenConfirm(false);
                    }}
                  />
                </div>
              </div>
              <div className="mt-2 h-1 w-full border-b-4 border-secondary"></div>

              <div
                className={`flex justify-center font-semibold text-left pr-2 my-2 ${
                  keeperRecords ? "text-primary" : "text-gray-500"
                }`}
              >
                {keeperRecords ? keeperRecords.name : "No File Chosen"}
              </div>
              <div className="flex justify-center">
                <label
                  className={
                    "text-center text-sm bg-primary text-white py-1 px-3 border font-medium rounded-full shadow-md transition duration-500 cursor-pointer hover:scale-90"
                  }
                >
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    className="hidden"
                    disabled={false}
                    onChange={handleFileChange}
                  />
                  Choose File
                </label>
              </div>
            </div>
          </Modal>
        )}
        {userPermissions.includes(viewPermission) ? (
          <div className="box-content">
            <DownloadButton
              buttonValue={isLoadingExport ? "Downloading..." : "Download File"}
              disabled={isLoadingExport}
              onClick={() => handleExportButton()}
            />
          </div>
        ) : (
          ""
        )}
      </div>
      {openConfirm && (
        <ConfirmBoxModal
          ModalTitle={"Upload Keeper Records"}
          qConfirmation={`Are you sure you want to upload this file?`}
          isOpen={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onClickFunction={() => handleSubmit()}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default ProducerCodeActionGroup;
